// Per-PR embed override for cross-app review. Appending `?sandbox:<app>=<slug>` to any presenter URL
// (e.g. `?sandbox:reports=pr-154`) repoints that same-origin embed at its own per-PR sandbox build, so
// a reviewer can see host + dependency PRs running together before either merges. `<app>` is the embed's
// first same-origin path segment (reports, team-management, onboarding, plugins, ai — every base routed
// through resolveSameOriginBase in url.utils.js); `<slug>` is a build id like `pr-154`. Resolves to the
// same-origin path `/sandbox/<app>/<slug>`. Non-prod hosts only, and seeded once from the query into
// sessionStorage so it survives in-app navigation.
// `local.ahaslide.com` is the loopback alias documented in .env.local.example.
const ALLOWED_HOSTS = ['presenter.dev.ahaslide.com', 'localhost', '127.0.0.1', 'local.ahaslide.com']
const STORAGE_KEY = 'presenter-sandbox-apps'
const KEY_RE = /^sandbox:([a-z][a-z0-9-]{0,31})$/
const APP_RE = /^[a-z][a-z0-9-]{0,31}$/
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/

export const isAllowedHost = (hostname) => ALLOWED_HOSTS.some((h) => hostname === h || hostname.endsWith(`.${h}`))

const parseMap = (search) => {
  const map = {}
  try {
    new URLSearchParams(search).forEach((value, key) => {
      const m = KEY_RE.exec(key)
      if (m && SLUG_RE.test(value)) map[m[1]] = value
    })
  } catch (e) {
    return map
  }
  return map
}

// Any script on the origin can write sessionStorage, and these values land in a URL path.
const readStored = () => {
  const map = {}
  try {
    const raw = JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {}
    Object.keys(raw).forEach((app) => {
      if (APP_RE.test(app) && SLUG_RE.test(raw[app])) map[app] = raw[app]
    })
  } catch (e) {
    return map
  }
  return map
}

const writeStored = (map) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch (e) {
    // sessionStorage unavailable (private mode, quota) — the seed still applies to this call.
  }
}

export const resetSandboxAppsForTests = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // sessionStorage unavailable — nothing was stored either.
  }
}

export const sandboxBaseForApp = (app, location = window.location) => {
  if (!app || !isAllowedHost(location.hostname)) return null
  const seeded = parseMap(location.search)
  let map = readStored()
  if (Object.keys(seeded).length) {
    map = { ...map, ...seeded }
    writeStored(map)
  }
  const slug = Object.prototype.hasOwnProperty.call(map, app) ? map[app] : null
  return slug ? `/sandbox/${app}/${slug}` : null
}
